/**
 * navConfig.js —— 顶部导航（TopNavBar）的配置化数据层。
 *
 * 数据源优先级：
 *   1) 后端菜单树（Vuex `permission/menuTree` ← `queryPermissionsByUser()`，
 *      即 GET /sys/permission/getUserPermissionByToken），由路由守卫在进入页面前拉好；
 *   2) 本地兜底字典 `NAV_FALLBACK_*` —— 后端未配置或配置不可用时，保持既有 UI 不变。
 *
 * 两者最终归一到同一个 `NavNode` 形状，组件只需一个渲染分支：
 *   { key, path, label, i18nKey, disabled, children }
 *
 * ## 后端节点契约（每个节点）
 *   {
 *     path: "/explore/FullDbSearch",   // 绝对路径，无 :param；必须能命中客户端已注册路由
 *     name: "FullDbSearch",            // 路由名，建议与 router/i18nUtil.js 的键一致
 *     hidden: false,                   // true → 不渲染
 *     route: "1",                      // "0" → 不渲染（与权限菜单既有语义一致）
 *     meta: {
 *       title: "总库检索",              // 中文兜底文案
 *       i18nKey: "article",            // 优先用它从 menu.i18n.js 取中英文
 *       disabled: false,               // true → 置灰不可点（如「视频」）
 *       activeMenu: "/book"            // 可选：详情页归属的导航路径，由路由 meta 提供
 *     },
 *     children: []                     // 最多两级；三级及以下会被并入同级并告警
 *   }
 * 说明：本次契约不含 `layouts/*` 布局层与相对子路径，前端不做路径拼接。
 */

import { ROUTE_META_LANG_KEY } from '@/router/i18nUtil'
import { TITLE_I18N_KEYS } from '@/i18n/modules/menu.i18n'

/** 后端导航的最低「路由可达率」：低于该比例视为配置不可用，整体回退本地字典。 */
export const NAV_RESOLVE_GATE = 0.5

/** 首页在部分菜单树里用 `/` 或 RuoYi 风格路径表达，统一归一为客户端真实路由 */
export const NAV_HOME_PATH = '/home'
export const HOME_PATH_ALIASES = ['/', '/dashboard/analysis', '/dashboard/workplace']
export const HOME_TITLE_ALIASES = ['首页', 'Home', 'home']

/**
 * eastview 主题兜底导航（文字取自 menu.i18n.js）。
 * `i18nKey` 与后端契约的 `meta.i18nKey` 同名，因此两个来源共用一套文案解析。
 */
export const NAV_FALLBACK_EASTVIEW = [
  { i18nKey: 'home', path: '/home' },
  {
    i18nKey: 'explore',
    path: '/explore',
    children: [
      { i18nKey: 'article', path: '/explore/FullDbSearch' },
      { i18nKey: 'subDb', path: '/explore/ProductExplore' }
    ]
  },
  { i18nKey: 'newspaper', path: '/newspaper' },
  { i18nKey: 'journal', path: '/journal' },
  // 视频暂未开放：保留入口但置灰，与后端 meta.disabled 同一语义
  { i18nKey: 'video', path: '/video', disabled: true },
  { i18nKey: 'book', path: '/book' },
  // 图书集：与产品检索同源（都是 _shared/ExplorePage），用单独路径承载列表入口
  { i18nKey: 'bookCollection', path: '/explore/BookCollectionExplore' },
  { i18nKey: 'subject', path: '/subject' }
]

/** qiushi 主题兜底导航：无「探索」模块，全为一级平铺项 */
export const NAV_FALLBACK_QIUSHI = [
  { i18nKey: 'home', path: '/home' },
  { i18nKey: 'book', path: '/book' },
  { i18nKey: 'bookCollection', path: '/explore/BookCollectionExplore' }
]

/**
 * 节点是否参与导航渲染。
 * 与 mixins/menuMix.js 的可见性判定保持同一语义，避免两处规则漂移。
 * @param {object} node 菜单节点
 * @returns {boolean}
 */
export function isNavNodeVisible(node) {
  return !!node && !node.hidden && node.route !== '0'
}

/**
 * 归一首页路径：菜单树可能用 `/` 等别名表达首页，需映射到客户端真实路由 `/home`。
 * 仅在路径本身是已知别名（或路径缺失但标题表明是首页）时改写，避免误伤同名节点。
 * @param {string} path 原始路径
 * @param {string} title 节点标题
 * @returns {string}
 */
export function normalizeNavPath(path, title) {
  const raw = path || ''
  if (HOME_PATH_ALIASES.indexOf(raw) !== -1) return NAV_HOME_PATH
  if (!raw && HOME_TITLE_ALIASES.indexOf(title) !== -1) return NAV_HOME_PATH
  return raw
}

/**
 * 解析节点文案，回退链：
 *   meta.i18nKey → ROUTE_META_LANG_KEY[name] → TITLE_I18N_KEYS[meta.title] → meta.title → name → path
 * 中间两步让功能在后端尚未下发 i18nKey 时也能立即生效（否则切英文会漏出中文）。
 * @param {object} node 后端菜单节点
 * @param {(key: string) => string} keyToLabel i18n key → 当前语言文案
 * @returns {string}
 */
export function resolveNavNodeLabel(node, keyToLabel) {
  const meta = node.meta || {}
  const title = meta.title || node.title || ''
  const i18nKey =
    meta.i18nKey || ROUTE_META_LANG_KEY[node.name] || TITLE_I18N_KEYS[title] || ''
  const translated =
    i18nKey && typeof keyToLabel === 'function' ? keyToLabel(i18nKey) : ''
  return translated || title || node.name || node.path || ''
}

/** 构造统一的导航项（key 与 path 同值，便于 el-menu 的 index 与 v-for 的 key 都用 path） */
function toNavNode(path, label, i18nKey, disabled, children) {
  return {
    key: path,
    path,
    label,
    i18nKey: i18nKey || '',
    disabled: !!disabled,
    children: children || []
  }
}

/**
 * 收集二级子节点；三级及以下并入同级，避免组件只渲染两层而静默丢菜单。
 * @param {object} parent 父节点
 * @param {(key: string) => string} keyToLabel
 * @param {Array} bucket 输出数组
 */
function collectBackendChildren(parent, keyToLabel, bucket) {
  const children = Array.isArray(parent.children) ? parent.children : []
  children.forEach((child) => {
    if (!isNavNodeVisible(child)) return
    const meta = child.meta || {}
    const title = meta.title || child.title || ''
    const path = normalizeNavPath(child.path, title)
    // 自身可跳转就先输出，保证三级展开后顺序仍然是「父在前、子在后」
    if (path) {
      bucket.push(
        toNavNode(path, resolveNavNodeLabel(child, keyToLabel), meta.i18nKey, meta.disabled, [])
      )
    }
    const hasGrandchildren =
      Array.isArray(child.children) && child.children.some(isNavNodeVisible)
    if (hasGrandchildren) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(
          '[TopNavBar] 菜单层级超过两级，其子项已并入同级渲染：',
          title || path || child.name
        )
      }
      collectBackendChildren(child, keyToLabel, bucket)
    }
  })
}

/**
 * 后端菜单树 → NavNode[]（最多两级）。
 * @param {Array} menuTree Vuex `permission/menuTree`
 * @param {(key: string) => string} keyToLabel
 * @returns {Array}
 */
export function buildNavFromMenuTree(menuTree, keyToLabel) {
  const list = Array.isArray(menuTree) ? menuTree : []
  const nodes = []
  list.forEach((node) => {
    if (!isNavNodeVisible(node)) return
    const meta = node.meta || {}
    const title = meta.title || node.title || ''
    const path = normalizeNavPath(node.path, title)
    const children = []
    if (Array.isArray(node.children) && node.children.length) {
      collectBackendChildren(node, keyToLabel, children)
    }
    // 既无路径又无子项的节点没有任何可渲染内容，直接丢弃
    if (!path && !children.length) return
    nodes.push(
      toNavNode(path, resolveNavNodeLabel(node, keyToLabel), meta.i18nKey, meta.disabled, children)
    )
  })
  return nodes
}

/**
 * 本地兜底字典 → NavNode[]。
 * @param {Array} source NAV_FALLBACK_* 之一
 * @param {(key: string) => string} keyToLabel
 * @returns {Array}
 */
export function buildNavFromFallback(source, keyToLabel) {
  const label = (key) => {
    const translated = key && typeof keyToLabel === 'function' ? keyToLabel(key) : ''
    return translated || key || ''
  }
  return (source || []).map((item) => {
    const children = (item.children || []).map((child) =>
      toNavNode(child.path || '', label(child.i18nKey), child.i18nKey, child.disabled, [])
    )
    return toNavNode(
      item.path || '',
      label(item.i18nKey),
      item.i18nKey,
      item.disabled,
      children
    )
  })
}

/**
 * 取出全部**叶子**路径（可跳转项）。分组父级自身的 path 不算叶子——
 * Element UI 的水平菜单无法高亮 submenu，分组必须投影到其子项。
 * @param {Array} items NavNode[]
 * @returns {string[]}
 */
export function collectLeafPaths(items) {
  const paths = []
  ;(items || []).forEach((item) => {
    if (!item) return
    if (item.children && item.children.length) {
      item.children.forEach((child) => {
        if (child && child.path) paths.push(child.path)
      })
      return
    }
    if (item.path) paths.push(item.path)
  })
  return paths
}

/**
 * 判定后端导航是否可用：叶子路径命中客户端已注册路由的比例达到阈值才算可用。
 *
 * 采用「路由事实」而非硬编码白名单：后端把菜单配好后自动接管，
 * 未配置时（例如仍是报纸产品的 `search-pageSearch` 菜单树）整体回退本地字典，保证零回归。
 * `disabled` 项豁免——如「视频」本就没有注册路由，但必须照常渲染。
 *
 * @param {Array} items NavNode[]
 * @param {Set<string>} routePaths 已注册路由路径集合
 * @param {number} [gate] 最低可达率
 * @returns {boolean}
 */
export function isBackendNavUsable(items, routePaths, gate = NAV_RESOLVE_GATE) {
  const leafPaths = []
  ;(items || []).forEach((item) => {
    if (!item || item.disabled) return
    const children = (item.children || []).filter((child) => child && !child.disabled)
    if (children.length) {
      children.forEach((child) => child.path && leafPaths.push(child.path))
      return
    }
    if (item.path) leafPaths.push(item.path)
  })
  if (!leafPaths.length) return false
  const hits = leafPaths.filter((path) => routePaths && routePaths.has(path)).length
  return hits / leafPaths.length >= gate
}

/**
 * 取与当前路由最匹配的叶子路径（边界感知的最长前缀）。
 *
 * - 必须取「最长」：`/explore`（分组）与 `/explore/BookCollectionExplore`（一级叶子）
 *   共享前缀，取首个会把图书集页判成探索页，而分组无法被高亮 → 高亮整个清空。
 * - 边界感知（`candidate + '/'`）：避免 `/book` 误配未来的 `/bookmark` 类路径。
 * - 排除 `'/'`：否则它会匹配上所有路由。
 *
 * @param {string[]} leafPaths
 * @param {string} routePath
 * @returns {string} 未命中返回 ''
 */
export function longestLeafPrefix(leafPaths, routePath) {
  if (!routePath) return ''
  let best = ''
  ;(leafPaths || []).forEach((candidate) => {
    if (!candidate || candidate === '/') return
    const hit =
      routePath === candidate || routePath.indexOf(candidate + '/') === 0
    if (hit && candidate.length > best.length) best = candidate
  })
  return best
}

/**
 * 计算当前路由应高亮的导航 index。
 *
 * 解析顺序（顺序不可调换）：
 *   1. `activeMenu` 显式指定（路由 meta.activeMenu，用于详情页等无法用前缀推断归属的场景）
 *   2. 叶子精确匹配
 *   3. 分组精确匹配 → 投影到其首个叶子
 *   4. 叶子最长边界前缀匹配（覆盖 /book/detail/:id、/newspaper/pageDetail 等无独立菜单项的详情页）
 *   5. 回落 routePath —— Element UI 找不到该 index 会清空高亮，与改造前在未知路由上的表现一致
 *
 * 第 2 步必须早于第 4 步，且第 4 步取「最长」：`/explore`（分组）排在
 * `/explore/BookCollectionExplore`（一级叶子）之前，若改成「取首个前缀命中」，
 * 图书集页会被判成探索页，而分组无法被高亮 → 高亮整个清空。
 *
 * @param {Array} navItems NavNode[]
 * @param {string} routePath 当前路由 path
 * @param {string} [activeMenu] 路由 meta.activeMenu
 * @returns {string}
 */
export function resolveActiveIndex(navItems, routePath, activeMenu) {
  const items = navItems || []
  const path = routePath || ''
  const leafPaths = collectLeafPaths(items)
  const groupOf = (p) => {
    const group = items.find((n) => n && n.path === p && n.children && n.children.length)
    return group && group.children.length ? group.children[0].path : ''
  }

  if (activeMenu) {
    const hit = groupOf(activeMenu) || (leafPaths.indexOf(activeMenu) !== -1 ? activeMenu : '')
    if (hit) return hit
  }
  if (leafPaths.indexOf(path) !== -1) return path
  const grouped = groupOf(path)
  if (grouped) return grouped
  const prefix = longestLeafPrefix(leafPaths, path)
  if (prefix) return prefix
  return path
}
